import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import {
  Car, FileText, MapPin, MessageSquare, Calendar, CheckCircle, Star, Shield,
  ArrowRight, ArrowLeft, Play, Pause, X, Wrench, Camera, Fingerprint, Lock,
  BadgeCheck, TrendingUp, Bell, Sparkles, Building2,
} from 'lucide-react';

const DEMO_PASSPORT_URL = `${window.location.origin}/car-passport/public/738b78f3-7cf6-487a-9c2f-a2df12db53d4`;
const COVER_VIDEO_URL = '/videos/flix-cover.mp4';

/* ---------- Visuales de cada paso ---------- */

const VisualRequest = () => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-900 w-full max-w-md shadow-2xl">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center"><FileText className="w-4 h-4 text-purple-700" /></div>
      <div>
        <p className="text-sm font-bold">Nueva solicitud</p>
        <p className="text-xs text-slate-500">Carlos · Honda CR-V 2021</p>
      </div>
    </div>
    <div className="space-y-2.5 text-sm">
      <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Servicio</p>
        <p className="font-semibold">Cambio de llantas</p>
      </div>
      <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Descripción</p>
        <p className="text-slate-600 text-xs leading-relaxed">Las llantas de mi Honda CR-V se desgastaron de forma dispareja. Necesito cambio y revisar alineación.</p>
      </div>
      <div className="flex gap-2.5">
        <div className="flex-1 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Ubicación</p>
          <p className="font-semibold text-xs flex items-center gap-1"><MapPin className="w-3 h-3 text-purple-600" /> Escazú</p>
        </div>
        <div className="flex-1 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Urgencia</p>
          <p className="font-semibold text-xs">Esta semana</p>
        </div>
      </div>
    </div>
    <div className="mt-4 bg-purple-600 text-white text-center text-sm font-semibold rounded-lg py-2.5">Publicar solicitud →</div>
  </div>
);

const VisualNotify = () => (
  <div className="w-full max-w-md space-y-3">
    {[
      ['Taller Express', '1.2 km', 'hace 1 min'],
      ['Auto Service Pro', '2.8 km', 'hace 2 min'],
      ['Mecánica Central', '4.5 km', 'hace 4 min'],
    ].map(([name, dist, time], i) => (
      <div key={name} className="bg-white rounded-xl border border-slate-200 p-4 text-slate-900 shadow-xl flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 200}ms` }}>
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0"><Bell className="w-5 h-5 text-purple-700" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold">{name}</p>
          <p className="text-xs text-slate-500">Nueva solicitud cerca · {dist}</p>
        </div>
        <span className="text-[10px] text-slate-400 shrink-0">{time}</span>
      </div>
    ))}
    <p className="text-center text-xs text-slate-400 pt-1">Radio de notificación: 10–20 km</p>
  </div>
);

const VisualBid = () => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-900 w-full max-w-md shadow-2xl">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-9 h-9 bg-purple-600 rounded-lg flex items-center justify-center"><Wrench className="w-4 h-4 text-white" /></div>
      <div>
        <p className="text-sm font-bold">Taller Express responde</p>
        <p className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3 h-3 text-amber-500 fill-amber-500" /> 4.7 · 25 reseñas</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2.5 mb-3">
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Precio</p>
        <p className="font-mono-data font-bold text-emerald-700">₡185 000</p>
      </div>
      <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Tiempo</p>
        <p className="font-semibold text-sm">3 horas</p>
      </div>
    </div>
    <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-3 leading-relaxed">
      "Cotización todo incluido: llantas nuevas, balanceo y alineación. Garantía de 3 meses por escrito."
    </p>
  </div>
);

const VisualCompare = () => (
  <div className="w-full max-w-lg grid grid-cols-3 gap-3">
    {[
      ['Taller Express', '₡185 000', '4.7', true],
      ['Auto Service Pro', '₡198 000', '4.4', false],
      ['Auto Rápido', '₡176 000', '4.6', false],
    ].map(([name, price, rating, best]) => (
      <div key={name} className={`rounded-xl p-4 text-center ${best ? 'bg-purple-600 text-white shadow-2xl scale-105 border border-purple-400' : 'bg-white text-slate-900 border border-slate-200 shadow-xl'}`}>
        {best && <span className="text-[9px] font-bold uppercase tracking-widest bg-white/20 px-2 py-0.5 rounded-full">Elegida ✓</span>}
        <p className={`text-xs font-bold mt-2 ${best ? '' : 'text-slate-800'}`}>{name}</p>
        <p className={`font-mono-data font-bold text-sm mt-1.5 ${best ? '' : 'text-slate-900'}`}>{price}</p>
        <p className={`text-[11px] mt-1 flex items-center justify-center gap-0.5 ${best ? 'text-purple-100' : 'text-slate-500'}`}>
          <Star className={`w-3 h-3 ${best ? 'text-amber-300 fill-amber-300' : 'text-amber-500 fill-amber-500'}`} /> {rating}
        </p>
      </div>
    ))}
  </div>
);

const VisualAppointment = () => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-900 w-full max-w-md shadow-2xl text-center">
    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
      <Calendar className="w-7 h-7 text-emerald-600" />
    </div>
    <p className="text-lg font-bold">¡Cita confirmada!</p>
    <p className="text-sm text-slate-500 mb-4">Taller Express · Escazú</p>
    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-center justify-center gap-6">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Fecha</p>
        <p className="font-mono-data font-bold">Jue 12 Jun</p>
      </div>
      <div className="w-px h-8 bg-slate-200" />
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Hora</p>
        <p className="font-mono-data font-bold">9:00 AM</p>
      </div>
    </div>
    <p className="text-xs text-slate-400 mt-3 flex items-center justify-center gap-1"><Bell className="w-3 h-3" /> Recordatorio automático activado</p>
  </div>
);

const VisualTimeline = () => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-900 w-full max-w-md shadow-2xl">
    <p className="text-sm font-bold mb-4 flex items-center gap-2"><Wrench className="w-4 h-4 text-purple-600" /> Progreso de reparación</p>
    <div className="space-y-0">
      {[
        ['Vehículo recibido', 'done'],
        ['Diagnóstico', 'done'],
        ['En reparación', 'active'],
        ['Control de calidad', 'pending'],
        ['Listo para entrega', 'pending'],
      ].map(([label, status], i, arr) => (
        <div key={label} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${status === 'done' ? 'bg-emerald-100 text-emerald-600' : status === 'active' ? 'bg-amber-100 text-amber-600 animate-pulse' : 'bg-slate-100 text-slate-300'}`}>
              {status === 'done' ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-2 h-2 rounded-full bg-current" />}
            </div>
            {i < arr.length - 1 && <div className={`w-0.5 h-5 ${status === 'done' ? 'bg-emerald-200' : 'bg-slate-100'}`} />}
          </div>
          <div className="pb-3">
            <p className={`text-sm ${status === 'pending' ? 'text-slate-400' : 'font-semibold'}`}>{label}</p>
            {status === 'active' && (
              <div className="flex gap-1.5 mt-1.5">
                {['Antes', 'Durante'].map((t) => (
                  <span key={t} className="flex items-center gap-1 text-[9px] font-semibold uppercase bg-purple-50 border border-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    <Camera className="w-2.5 h-2.5" /> {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const VisualReview = () => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-900 w-full max-w-md shadow-2xl">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">C</div>
      <div>
        <p className="text-sm font-bold">Carlos Rodríguez</p>
        <p className="text-xs text-slate-500">Honda CR-V 2021 · Cambio de llantas</p>
      </div>
      <span className="ml-auto flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
        <BadgeCheck className="w-3 h-3" /> Verificado
      </span>
    </div>
    <div className="flex gap-0.5 mb-2.5">
      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-5 h-5 text-amber-500 fill-amber-500" />)}
    </div>
    <p className="text-sm text-slate-600 leading-relaxed italic">
      "Excelente servicio, mi Honda CR-V quedó perfecta. Me enviaron fotos del avance y todo quedó registrado en el Car Passport."
    </p>
  </div>
);

const VisualPassport = () => (
  <div className="passport-grid rounded-2xl border border-slate-200 text-slate-900 shadow-2xl overflow-hidden w-full max-w-md">
    <div className="bg-purple-600 px-5 py-4 text-white flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Fingerprint className="w-4 h-4 text-purple-200" />
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-purple-100">Car Passport</span>
      </div>
      <span className="font-mono-data tracking-widest text-sm bg-white/15 px-3 py-1 rounded-lg">XYZ-789</span>
    </div>
    <div className="p-5">
      <p className="text-sm font-bold mb-3">Honda CR-V 2021 · 14 servicios</p>
      <div className="space-y-2">
        <div className="flex items-center justify-between bg-purple-50 border-2 border-purple-300 rounded-lg px-3 py-2.5 animate-fade-up">
          <div>
            <p className="text-xs font-bold text-purple-900 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Cambio de llantas — NUEVO</p>
            <p className="text-[11px] text-slate-500">Taller Express · con fotos</p>
          </div>
          <span className="font-mono-data text-[11px] text-slate-400">12 Jun 2026</span>
        </div>
        {[['Mecánica general', 'Mecánica Central', '7 Feb 2026'], ['Aire acondicionado', 'Auto Rápido', '28 Ene 2026']].map(([s, t, d]) => (
          <div key={s} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
            <div>
              <p className="text-xs font-semibold text-slate-700">{s}</p>
              <p className="text-[11px] text-slate-500">{t}</p>
            </div>
            <span className="font-mono-data text-[11px] text-slate-400">{d}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-dashed border-slate-200 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Registro inmutable</span>
        <span className="font-mono-data text-purple-700">sha256: a94f•••e2c1</span>
      </div>
    </div>
  </div>
);

const VisualShare = () => (
  <div className="flex flex-col sm:flex-row items-center gap-6 w-full max-w-lg justify-center">
    <div className="bg-white rounded-2xl p-5 shadow-2xl border border-slate-200">
      <QRCode value={DEMO_PASSPORT_URL} size={150} />
      <p className="text-[10px] text-slate-500 text-center mt-2 font-semibold">Escanéalo ahora — es real</p>
    </div>
    <div className="space-y-3 text-left">
      {[
        ['Compradores', 'Verifican el historial antes de comprar'],
        ['INS y aseguradoras', 'Valoración con datos confiables'],
        ['Nuevo dueño', 'El passport se transfiere con el vehículo'],
      ].map(([who, what]) => (
        <div key={who} className="flex items-start gap-2.5">
          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">{who}</p>
            <p className="text-xs text-slate-400">{what}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const VisualStats = () => (
  <div className="w-full max-w-2xl">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
      {[
        ['67', 'Talleres certificados'],
        ['155', 'Solicitudes gestionadas'],
        ['1 583', 'Reseñas verificadas'],
        ['+15%', 'Valor de reventa'],
      ].map(([v, l]) => (
        <div key={l} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400 font-heading">{v}</p>
          <p className="text-[11px] text-slate-400 mt-1">{l}</p>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
      <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-purple-400" /> Listo para INS y aseguradoras</span>
      <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-purple-400" /> Preparado para MOPT y RTV</span>
    </div>
  </div>
);

/* ---------- Slides ---------- */

const SLIDES = [
  {
    phase: 'Bienvenida', icon: Car,
    title: 'Así funciona Flix',
    subtitle: 'El recorrido completo: de la solicitud del cliente al Car Passport verificado. Usa las flechas ← → o presiona Play.',
    Visual: null,
  },
  {
    phase: 'El Cliente', icon: FileText,
    title: 'Publica su necesidad en 3 minutos',
    subtitle: 'El cliente describe qué necesita su vehículo, dónde está y cuándo lo quiere. Sin llamadas, sin vueltas.',
    Visual: VisualRequest,
  },
  {
    phase: 'El Taller', icon: Bell,
    title: 'Los talleres cercanos la reciben al instante',
    subtitle: 'Cada taller certificado en la zona recibe una notificación en tiempo real con los detalles de la solicitud.',
    Visual: VisualNotify,
  },
  {
    phase: 'El Taller', icon: MessageSquare,
    title: 'Responden con su mejor oferta',
    subtitle: 'Precio, tiempo estimado y garantía por escrito. Los talleres compiten por dar el mejor servicio.',
    Visual: VisualBid,
  },
  {
    phase: 'El Cliente', icon: TrendingUp,
    title: 'Compara y elige con total transparencia',
    subtitle: 'Ofertas lado a lado: precio, reputación y tiempos. El cliente decide con información real, no promesas.',
    Visual: VisualCompare,
  },
  {
    phase: 'El Cliente', icon: Calendar,
    title: 'Agenda la cita al instante',
    subtitle: 'Fecha y hora coordinadas en la plataforma, con confirmación inmediata del taller y recordatorios.',
    Visual: VisualAppointment,
  },
  {
    phase: 'El Taller', icon: Wrench,
    title: 'Gestiona la reparación etapa por etapa',
    subtitle: 'El taller avanza el trabajo por etapas y sube fotos de evidencia: antes, durante y después.',
    Visual: VisualTimeline,
  },
  {
    phase: 'Confianza', icon: Star,
    title: 'Servicio completado, reseña verificada',
    subtitle: 'Solo quien recibió el servicio puede calificar. Cada reseña queda ligada a una transacción real.',
    Visual: VisualReview,
  },
  {
    phase: 'Confianza', icon: Fingerprint,
    title: 'El Car Passport se actualiza solo',
    subtitle: 'Cada servicio queda registrado automáticamente en la identidad digital del vehículo. Inmutable y verificable.',
    Visual: VisualPassport,
  },
  {
    phase: 'El Negocio', icon: BadgeCheck,
    title: 'Compartible y verificable por cualquiera',
    subtitle: 'Un enlace público con QR permite a compradores y aseguradoras verificar el historial. Sin cuenta, sin fricciones.',
    Visual: VisualShare,
  },
  {
    phase: 'El Negocio', icon: TrendingUp,
    title: 'Un ecosistema que genera valor',
    subtitle: 'Más transparencia → más confianza → vehículos que valen más y talleres con más clientes. Todos ganan.',
    Visual: VisualStats,
  },
];

/* ---------- Página ---------- */

export default function DemoTourPage() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, SLIDES.length - 1)), []);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, navigate]);

  useEffect(() => {
    if (!playing) return;
    if (idx >= SLIDES.length - 1) { setPlaying(false); return; }
    const t = setTimeout(next, 8000);
    return () => clearTimeout(t);
  }, [playing, idx, next]);

  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;
  const Icon = slide.icon;

  return (
    <div className="min-h-screen bg-slate-950 passport-dark-grid text-white flex flex-col relative" data-testid="demo-tour-page">
      {/* Video background — solo en portada */}
      {idx === 0 && (
        <div className="absolute inset-0 z-0 overflow-hidden" data-testid="demo-video-bg">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            data-testid="demo-cover-video"
          >
            <source src="/videos/flix-cover.webm" type="video/webm" />
            <source src={COVER_VIDEO_URL} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-slate-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-slate-950/80" />
        </div>
      )}

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 border-b border-white/10">
        <button onClick={() => navigate('/')} className="flex items-center space-x-2.5" data-testid="demo-logo-btn">
          <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold font-heading">Flix</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400 bg-purple-500/10 border border-purple-500/30 px-2 py-0.5 rounded-full ml-2">Demo Tour</span>
        </button>
        <button onClick={() => navigate('/')} className="text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 text-sm" data-testid="demo-exit-btn">
          Salir <X className="w-4 h-4" />
        </button>
      </header>

      {/* Progress bar */}
      <div className="relative z-10 h-1 bg-white/5">
        <div className="h-full bg-purple-500 transition-all duration-500" style={{ width: `${((idx + 1) / SLIDES.length) * 100}%` }} />
      </div>

      {/* Slide */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10 text-center" key={idx}>
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-400 mb-5 animate-fade-up" data-testid="demo-slide-phase">
          <Icon className="w-4 h-4" /> {slide.phase}
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 max-w-3xl leading-tight animate-fade-up delay-100" data-testid="demo-slide-title">
          {slide.title}
        </h1>
        <p className="text-slate-400 max-w-xl leading-relaxed mb-10 animate-fade-up delay-200" data-testid="demo-slide-subtitle">
          {slide.subtitle}
        </p>

        <div className="flex justify-center w-full animate-fade-up delay-300 px-2">
          {slide.Visual ? <slide.Visual /> : (
            <div className="flex items-center gap-3">
              {['El Cliente', 'El Taller', 'Confianza', 'El Negocio'].map((p, i) => (
                <span key={p} className="text-xs font-semibold bg-white/5 border border-white/15 px-4 py-2 rounded-full text-slate-300 animate-fade-up" style={{ animationDelay: `${300 + i * 150}ms` }}>{p}</span>
              ))}
            </div>
          )}
        </div>

        {isLast && (
          <div className="flex flex-col sm:flex-row gap-3 mt-10 animate-fade-up delay-400">
            <Button onClick={() => navigate('/auth')} size="lg" className="rounded-full bg-purple-600 hover:bg-purple-700 px-8 active:scale-95 transition-transform" data-testid="demo-final-cta">
              Explorar la app <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => setIdx(0)} size="lg" variant="outline" className="rounded-full border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white px-8" data-testid="demo-restart-btn">
              Ver de nuevo
            </Button>
          </div>
        )}
      </main>

      {/* Controls */}
      <footer className="px-6 py-5 border-t border-white/10">
        <div className="max-w-3xl mx-auto w-full flex items-center justify-between">
        <Button
          variant="outline" size="sm" onClick={prev} disabled={idx === 0}
          className="rounded-full border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30"
          data-testid="demo-prev-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Anterior
        </Button>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setPlaying(!playing)}
            className="w-9 h-9 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center transition-colors active:scale-95"
            data-testid="demo-play-btn"
            title={playing ? 'Pausar' : 'Reproducir automáticamente'}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
          <div className="hidden sm:flex gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-6 bg-purple-500' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
                data-testid={`demo-dot-${i}`}
              />
            ))}
          </div>
          <span className="text-xs text-slate-500 font-mono-data">{idx + 1}/{SLIDES.length}</span>
        </div>

        <Button
          size="sm" onClick={next} disabled={isLast}
          className="rounded-full bg-purple-600 hover:bg-purple-700 disabled:opacity-30 active:scale-95 transition-transform"
          data-testid="demo-next-btn"
        >
          Siguiente <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        </div>
      </footer>
    </div>
  );
}
